import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const faqs = [
    {
        question: 'Why is my car AC blowing air but not cold air?',
        answer: 'This often points to low refrigerant, a leak, compressor trouble, condenser problems, cooling fan issues, or climate control faults.'
    },
    {
        question: 'Can a dirty cabin air filter make the AC feel weak?',
        answer: 'Yes. A clogged cabin air filter can reduce airflow from the vents and make the AC feel much less effective.'
    },
    {
        question: 'Why is my AC colder while driving than when stopped?',
        answer: 'That can happen if the cooling fan is not working properly or the condenser is not releasing heat efficiently at idle.'
    },
    {
        question: 'Can CarFido Assistant help me understand the issue first?',
        answer: 'Yes. CarFido Assistant can guide you based on symptoms, suggest simple checks, show repair cost context, and help you log the issue in your vehicle history.'
    }
];

const Blog11 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 11;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Why Is My Car AC Not Blowing Cold Air? 8 Common Causes and Fixes
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            If your car AC is blowing warm air, weak air, or air that never gets properly cold, the problem could be anything from low refrigerant to a clogged filter, bad compressor, or electrical fault.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 9 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/11_1.jpeg"
                            alt="Driver dealing with car AC not blowing cold air"
                            title="Why Is My Car AC Not Blowing Cold Air?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: home_5.jpeg (placeholder)
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#ac-summary" className="text-gray-900 no-underline hover:underline">AC issue summary</a></li>
                            <li><a href="#why-it-happens" className="text-gray-900 no-underline hover:underline">Why this happens</a></li>
                            <li><a href="#cause-1" className="text-gray-900 no-underline hover:underline">Low refrigerant</a></li>
                            <li><a href="#cause-3" className="text-gray-900 no-underline hover:underline">Faulty AC compressor</a></li>
                            <li><a href="#cause-4" className="text-gray-900 no-underline hover:underline">Clogged cabin air filter</a></li>
                            <li><a href="#what-you-can-check" className="text-gray-900 no-underline hover:underline">What you can check first</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            A car AC that stops blowing cold air can make daily driving miserable, especially in hot weather. Sometimes the problem is simple, like weak airflow caused by a dirty cabin air filter. Other times, it may point to a refrigerant leak, compressor trouble, or an electrical fault inside the climate control system.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            The key is to understand whether the problem is related to airflow, refrigerant pressure, cooling performance, or controls. That helps you narrow down what is worth checking first before spending money on repairs.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide covers eight common reasons your car AC is not blowing cold air, along with what the symptoms may suggest and what the fix often involves.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Why is my car AC not blowing cold air?</strong><br />
                            Common causes include low refrigerant, a refrigerant leak, a bad compressor, a clogged cabin air filter, condenser problems, cooling fan issues, electrical faults, or blend door and climate control problems.
                        </p>
                    </section>

                    <section id="ac-summary" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Car AC Problem Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Possible Cause</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">What It Can Lead To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Low refrigerant</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Weak cooling or air that is no longer cold</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Refrigerant leak</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Cooling that gets worse over time or stops suddenly</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Faulty compressor</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Warm air and poor refrigerant circulation</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Dirty cabin air filter</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Weak airflow from the vents</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900 border-b border-gray-200">Condenser or fan issue</td>
                                        <td className="py-3.5 px-4 text-gray-600 border-b border-gray-200">Poor cooling, especially at idle or in traffic</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-happens" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why This Happens</h2>
                        <p className="mb-4 text-gray-700">
                            Your car’s AC system depends on refrigerant, airflow, heat exchange, and correct control signals. If any of those parts stop working properly, the system may still blow air, but that air may not feel cold.
                        </p>
                        <p className="mb-0 text-gray-700">
                            That is why it helps to notice whether the problem is warm air, weak airflow, cooling only while driving, or AC that works intermittently. Each pattern can point toward a different likely cause.
                        </p>
                    </section>

                    <section id="cause-1" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">1. Low Refrigerant</h2>
                        <p className="mb-0 text-gray-700">
                            Low refrigerant is one of the most common reasons a car AC stops blowing properly cold air. Refrigerant is what allows the system to remove heat from the cabin air. If the level gets too low, cooling performance drops.
                        </p>
                    </section>

                    <section id="cause-3" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">3. Faulty AC Compressor</h2>
                        <p className="mb-0 text-gray-700">
                            The AC compressor circulates refrigerant through the system. If it fails, or the compressor clutch does not engage, the AC may blow warm air or stop cooling completely.
                        </p>
                    </section>

                    <section id="cause-4" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">4. Clogged Cabin Air Filter</h2>
                        <p className="mb-0 text-gray-700">
                            Sometimes the problem is not temperature but airflow. A dirty cabin air filter can restrict air coming through the vents, which makes the AC feel weak even if the system is still cooling.
                        </p>
                    </section>

                    <section id="what-you-can-check" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">What You Can Check First</h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Make sure the AC is switched on and set to cold</li>
                            <li>Check whether airflow is weak or strong</li>
                            <li>Inspect the cabin air filter if it is easy to access</li>
                            <li>Notice whether the AC is colder while driving than while stopped</li>
                            <li>Listen for unusual clicking or compressor noise</li>
                            <li>Look for obvious dirt or blockage near the condenser area</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">How CarFido Assistant Helps</h2>

                            <p className="mb-3.5 text-gray-700">
                                If your car AC is not blowing cold air, CarFido Assistant can help you figure out what the issue might be before you head to a workshop.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido helps you:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Diagnose the issue using symptom-based guidance</li>
                                <li>Get DIY video checks for filters, vents, and basic inspection</li>
                                <li>Understand rough repair cost estimates</li>
                                <li>Add the issue to your vehicle history log</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">Frequently Asked Questions</h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Final Thoughts</h2>
                        <p className="mb-4 text-gray-700">
                            A car AC that is not blowing cold air can be caused by several different issues, from a simple airflow restriction to a refrigerant leak or a bad compressor.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Understanding the likely cause can help you decide what to inspect first and whether the repair may be minor or more involved.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If your AC is blowing warm air, weak air, or cooling inconsistently, it is better to investigate early instead of waiting for the system to fail completely.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#ac-summary" className="no-underline text-gray-600 hover:text-gray-900 text-sm">AC summary</a></li>
                            <li><a href="#cause-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Low refrigerant</a></li>
                            <li><a href="#cause-3" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Bad compressor</a></li>
                            <li><a href="#cause-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Cabin air filter</a></li>
                            <li><a href="#what-you-can-check" className="no-underline text-gray-600 hover:text-gray-900 text-sm">What to check first</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog11;
